/**
 * <TutorLiveActivityTab> — l'onglet de l'activité ouverte, sur la première ligne du centre du live du masteur, après
 * les onglets de l'élève (molecule).
 *
 * Wajih, 05/11 : le centre tient sur deux lignes, jamais fusionnées — l'élève, ses onglets et celui de l'activité ;
 * dessous, les vues de l'activité (`<TutorLiveActivityTabs>`). L'onglet porte ce qui est à l'activité : son titre, qui
 * ne disparaît jamais (il se coupe, à toute largeur, colonnes repliées ou dépliées), son chrono et son statut (ils ont
 * quitté la barre du bas), et la croix qui la ferme. Il se touche comme un onglet : depuis la visio ou le profil, il
 * rouvre l'activité.
 *
 * La carte blanche, son ombre et le liseré de l'univers distinguent l'activité de la page sans en faire LA carte ;
 * quand une autre vue est ouverte, elle passe encastrée.
 *
 * Contrôlée : la vue ouverte et la fermeture arrivent en props.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIActivityDurationTone, type UIActivityStatus } from "../../../ports/primary/ui/types/activity"
import { Badge } from "../../atoms/Badge"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { ActivityStatusBadge } from "../ActivityStatusBadge"
import { DURATION_VARIANT } from "../ActivityRow/ActivityRow"
import { Card } from "../Card"

export type TutorLiveActivityTabProps = {
    /** Le titre de l'activité ouverte. */
    title: string
    /** Son chrono, au ton du temps prévu. Absent : rien. */
    chrono?: { label: string; tone?: UIActivityDurationTone }
    /** Son statut. Absent : rien. */
    status?: UIActivityStatus
    /** Une vue de l'activité est ouverte. Défaut `true`. */
    selected?: boolean
    /** Rouvrir l'activité depuis un autre onglet. Absent : l'onglet ne se touche pas. */
    onPress?: () => void
    /** Fermer l'activité. Absent : pas de croix. */
    onClose?: () => void
    testID?: string
}

const TEXTS = { close: "Fermer l'activité" }

/** Le titre garde toujours de quoi se lire : le chrono et le statut ne le poussent jamais hors de l'onglet. */
const TITLE_MIN = 72

export function TutorLiveActivityTab(props: TutorLiveActivityTabProps) {
    const { title, chrono, status, selected = true, onPress, onClose, testID } = props
    return (
        <Card
            p="0"
            leftBorderColor="role"
            elevation={selected ? "card" : "none"}
            background={selected ? "surface-raised" : "surface-subtle"}
            pressable={onPress != null && !selected}
            onPress={onPress}
            style={styles.card}
            testID={testID}
        >
            <Icon name="book-open" size={16} color="role" />
            <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1} style={styles.title}>
                {title}
            </Text>
            {chrono != null && (
                <View style={styles.fixed}>
                    <Badge
                        text={chrono.label}
                        variant={chrono.tone != null ? DURATION_VARIANT[chrono.tone] : "neutral"}
                        iconName="stopwatch"
                        tinted
                        truncate={false}
                    />
                </View>
            )}
            {status != null && (
                <View style={styles.fixed}>
                    <ActivityStatusBadge status={status} />
                </View>
            )}
            {onClose != null ? (
                <IconButton
                    iconName="times"
                    shape="square"
                    size={28}
                    iconSize={16}
                    appearance="transparent"
                    iconColor="text-muted"
                    onPress={onClose}
                    accessibilityLabel={TEXTS.close}
                />
            ) : (
                <View style={styles.end} />
            )}
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    card: {
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        flexShrink: 1,
        minWidth: 0,
        minHeight: 36,
        paddingLeft: theme.spacing["3"],
        paddingRight: theme.spacing["1"],
        borderRadius: theme.radius.md,
    },
    title: { flexShrink: 1, minWidth: TITLE_MIN },
    fixed: { flexShrink: 0 },
    end: { width: theme.spacing["2"] },
}))
